import { useEffect, useState, useCallback, useRef } from "react";
import {
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Spin,
  Button,
  Space,
} from "antd";
import { SaveOutlined, StarFilled } from "@ant-design/icons";
import apiHelpers from "./data";
import { STORES, hashForLabel } from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";

const labelForHash = (hash) => STORES.find((s) => s.hash === hash)?.label || "";

const STORE_OPTIONS = STORES.map((s) => ({
  value: s.hash,
  label: `${s.label} — ${s.name} (${s.hash})`,
}));

const emptyForm = (defaultStoreHash) => ({
  store_hash: defaultStoreHash || hashForLabel("UAT") || "",
  bamvideo_id: "",
  title: "",
  description: "",
  is_default: 0,
});

const normalizeIncoming = (data) => ({
  store_hash: data.storeHash || data.store_hash || "",
  store_label: data.storeLabel || data.store_label || "",
  bamvideo_id: data.bamvideoId || data.bamvideo_id || "",
  title: data.title || "",
  description: data.description || "",
  is_default: Number(data.isDefault ?? data.is_default) === 1 ? 1 : 0,
});

const diffPayload = (current, original) => {
  if (!original) return current;
  const patch = {};
  Object.keys(current).forEach((k) => {
    if (current[k] !== original[k]) patch[k] = current[k];
  });
  return patch;
};

const VideoFormModal = ({
  open,
  mode,
  videoId,
  defaultStoreHash,
  defaultStoreLabel,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [togglingDefault, setTogglingDefault] = useState(false);
  const initialValuesRef = useRef(null);

  const isCreate = mode === "create";
  const title = isCreate ? "New Bambuser Video" : "Edit Bambuser Video";

  const checkDirty = useCallback(() => {
    const current = JSON.stringify(form.getFieldsValue(true));
    setFormDirty(current !== initialValuesRef.current);
  }, [form]);

  const resetAll = useCallback(() => {
    form.resetFields();
    setOriginal(null);
    setFormDirty(false);
    initialValuesRef.current = null;
  }, [form]);

  useEffect(() => {
    if (!open) {
      resetAll();
      return;
    }

    if (isCreate || !videoId) {
      const initial = emptyForm(defaultStoreHash);
      form.resetFields();
      form.setFieldsValue(initial);
      setOriginal(null);
      setIsDefault(false);
      initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
      setFormDirty(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiHelpers.fetchVideoById(videoId);
        if (cancelled) return;
        const normalized = normalizeIncoming(data);
        form.resetFields();
        form.setFieldsValue(normalized);
        setOriginal(normalized);
        setIsDefault(Number(normalized.is_default) === 1);
        initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
        setFormDirty(false);
      } catch (err) {
        const mapped = apiHelpers.mapError(err);
        triggerNotification({
          type: "error",
          title: "Failed to load video",
          message: mapped.message,
        });
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, videoId, mode]);

  const handleToggleDefault = async (checked) => {
    if (!videoId) return;
    setTogglingDefault(true);
    try {
      await apiHelpers.updateVideo(videoId, { is_default: checked ? 1 : 0 });
      setIsDefault(checked);
      triggerNotification({
        type: "success",
        title: checked ? "Default set" : "Default removed",
        message: `${form.getFieldValue("title") || form.getFieldValue("bamvideo_id") || "Video"} ${checked ? "is now" : "is no longer"} the default.`,
      });
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      triggerNotification({ type: "error", title: "Failed to set default", message: mapped.message });
    } finally {
      setTogglingDefault(false);
    }
  };

  const handleOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const storeHash = String(values.store_hash || "").trim();
    const normalized = {
      store_hash: storeHash,
      store_label: labelForHash(storeHash),
      bamvideo_id: String(values.bamvideo_id || "").trim(),
      title: values.title === undefined ? "" : String(values.title).trim(),
      description: values.description === undefined ? "" : String(values.description).trim(),
      is_default: isCreate ? (Number(values.is_default) === 1 ? 1 : 0) : (isDefault ? 1 : 0),
    };

    setSaving(true);
    try {
      let saved;
      if (isCreate) {
        saved = await apiHelpers.createVideo({ ...normalized });
      } else {
        const patch = diffPayload(normalized, original);
        if (Object.keys(patch).length === 0) {
          triggerNotification({ type: "info", title: "No changes", message: "Nothing to save." });
          onClose?.();
          return;
        }
        saved = await apiHelpers.updateVideo(videoId, patch);
      }
      triggerNotification({
        type: "success",
        title: isCreate ? "Video created" : "Video updated",
        message: saved?.title || saved?.bamvideoId || "Saved successfully.",
      });
      onSaved?.(saved);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      if (mapped.code === "VALIDATION_ERROR" && Array.isArray(mapped.details)) {
        const fieldMap = { store_hash: "store_hash", store_label: "store_label", bamvideo_id: "bamvideo_id", title: "title", description: "description", is_default: "is_default" };
        const errors = mapped.details.filter((d) => d && fieldMap[d.path]).map((d) => ({ name: fieldMap[d.path], errors: [d.message] }));
        if (errors.length) form.setFields(errors);
      } else if (mapped.code === "DUPLICATE_KEY") {
        form.setFields([{ name: "bamvideo_id", errors: ["This Bambuser Video ID is already used for this store. Pick another."] }]);
      }
      triggerNotification({
        type: "error",
        title: isCreate ? "Create failed" : "Update failed",
        message: mapped.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      title={title}
      onClose={onClose}
      destroyOnClose
      maskClosable={false}
      width={600}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleOk}
            disabled={!isCreate && !formDirty}
          >
            Save
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" onValuesChange={checkDirty}>
          <Form.Item
            name="store_hash"
            label="Store"
            rules={[
              { required: true, message: "Store is required" },
              { max: 100, message: "Max 100 chars" },
            ]}
          >
            <Select showSearch options={STORE_OPTIONS} placeholder="Select a store" optionFilterProp="label" />
          </Form.Item>

          <Form.Item
            name="bamvideo_id"
            label="Bambuser Video ID"
            rules={[
              { required: true, message: "Bambuser Video ID is required" },
              { max: 255, message: "Max 255 chars" },
              { pattern: /^[a-zA-Z0-9_]+$/, message: "Only letters, numbers, and underscores are allowed" },
            ]}
          >
            <Input placeholder="e.g. bam_abc123" autoFocus={isCreate} />
          </Form.Item>

          <Form.Item
            name="title"
            label="Title"
            rules={[
              { max: 255, message: "Max 255 chars" },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === "") return Promise.resolve();
                  if (!/[a-zA-Z]/.test(value)) return Promise.reject("Must contain at least one letter. Only numbers not allowed.");
                  if (/[^a-zA-Z0-9\s_\-.,!?'"''""&()/:;]/.test(value)) return Promise.reject("Special characters like @#%^*+=<>{}[]\\|~` are not allowed.");
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="e.g. Dry Skin Routine" allowClear />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { max: 255, message: "Max 255 chars" },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === "") return Promise.resolve();
                  if (!/[a-zA-Z]/.test(value)) return Promise.reject("Must contain at least one letter. Only numbers not allowed.");
                  if (/[^a-zA-Z0-9\s_\-.,!?'"''""&()/:;]/.test(value)) return Promise.reject("Special characters like @#%^*+=<>{}[]\\|~` are not allowed.");
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.TextArea placeholder="e.g. A quick routine for dry, dehydrated skin" allowClear rows={3} maxLength={255} showCount />
          </Form.Item>

          {isCreate ? (
            <Form.Item style={{ marginBottom: 0 }}>
              <Space align="center">
                <Form.Item
                  name="is_default"
                  valuePropName="checked"
                  getValueFromEvent={(v) => (v ? 1 : 0)}
                  getValueProps={(v) => ({ checked: Number(v) === 1 })}
                  noStyle
                >
                  <Switch checkedChildren={<StarFilled />} />
                </Form.Item>
                <span style={{ fontSize: 13 }}>Set as default video</span>
              </Space>
            </Form.Item>
          ) : (
            <Space align="center">
              <Switch
                checked={isDefault}
                checkedChildren={<StarFilled />}
                loading={togglingDefault}
                onChange={handleToggleDefault}
              />
              <span style={{ fontSize: 13 }}>Set as default video</span>
            </Space>
          )}
        </Form>
      )}
    </Drawer>
  );
};

export default VideoFormModal;
